import { renameUserGoal, deleteUserGoal, updateGoalDifficulty } from '../api/userGoalApi';
import { updateGoalCategory } from '../api/userGoalApi';
import { toggleArchiveGoal } from '../api/userGoalApi';
import { addEntry, deleteEntry, renameEntry } from '../api/userEntryApi';
import {useState} from "react";
import { getUserGoal } from '../api/userGoalApi';
import { getGoalChecks } from '../api/userGoalCheckApi';
import { getCurrentMonthRange, getMonthRange } from '../utils/dateUtils';
import {
  getSocialList,
  getPublicGoalDetail,
  getPublicGoalChecks,
} from '../api/userApi';
import { updateGoalPrivacy } from '../api/userGoalApi';

export function useGoalActions(props) {
  const {
  setGoals,
  setGoalDetails,
  setGoalCheckDates,
  setOpenGoals,
  setViewedMonths,
  setEntryInputs,
  entryInputs,
  setIsLoadingGoalDetails,
  loadingGoalLock,
  setShowModal,
  setModalTitle,
  setUserList,
  setMyAchievements,
  setProfile,
  publicUserId,
  isPublicProfile,
} = props;

   const handleOpenList = async (userId, type) => {
  if (!userId) return; // Safety check
  try {
    const data = await getSocialList(userId, type);
    setUserList(data);
    setModalTitle(type === 'followers' ? 'Followers' : 'Following');
    setShowModal(true);
  } catch (err) {
    console.error('Failed to load list', err);
  }
};

  const loadGoalDetails = async (goalId, dateParams = null) => {
  if (!goalId) return;

  if (loadingGoalLock?.current?.[goalId]) return;
  if (loadingGoalLock) loadingGoalLock.current[goalId] = true;

  if (setIsLoadingGoalDetails) setIsLoadingGoalDetails(true);

  try {
    const range = dateParams?.from ? dateParams : getCurrentMonthRange();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let goalData;
    let checksData;

    if (isPublicProfile && publicUserId) {
      // Public profile view: fetch the target user's public goal data
      [goalData, checksData] = await Promise.all([
        getPublicGoalDetail(publicUserId, goalId, timeZone),
        getPublicGoalChecks(publicUserId, goalId, range.from, range.to),
      ]);
    } else {
      // Own home/goal page: fetch logged-in user's private goal data
      const [goalRes, checksRes] = await Promise.all([
        getUserGoal(goalId),
        getGoalChecks(goalId, range.from, range.to),
      ]);

      goalData = goalRes.data;
      checksData = checksRes.data;
    }

    setGoalDetails((prev) => ({
      ...prev,
      [goalId]: goalData,
    }));

    setGoalCheckDates((prev) => ({
      ...prev,
      [goalId]: Array.isArray(checksData)
        ? checksData.map((c) => c.date)
        : [],
    }));
  } catch (err) {
    console.error('Error loading goal details in hook:', err);
  } finally {
    if (setIsLoadingGoalDetails) setIsLoadingGoalDetails(false);
    if (loadingGoalLock) loadingGoalLock.current[goalId] = false;
  }
};

 const handleMonthNav = (goalId, direction) => {
  if (!setViewedMonths) return;

  // 1. We calculate the new values FIRST
  let newYear, newMonth;

  setViewedMonths((prev) => {
    const current = prev[goalId] || { 
      year: new Date().getFullYear(), 
      month: new Date().getMonth() 
    };

    newYear = current.year;
    newMonth = current.month;

    if (direction === 'PREV') {
      newMonth -= 1;
      if (newMonth < 0) { newMonth = 11; newYear -= 1; }
    } else {
      newMonth += 1;
      if (newMonth > 11) { newMonth = 0; newYear += 1; }

      const today = new Date();
      if (newYear > today.getFullYear() || (newYear === today.getFullYear() && newMonth > today.getMonth())) {
        return prev;
      }
    }

    // 2. NOW we have the correct newYear and newMonth
    const newRange = getMonthRange(newYear, newMonth);
    
    // 3. Trigger the API load for the historical checkmarks
    loadGoalDetails(goalId, newRange);

    return { ...prev, [goalId]: { year: newYear, month: newMonth } };
  });
};

  const handleRenameEntry = async (goalId, entryId, newText) => {
    // We remove the window.prompt because newText is already coming
    // from our beautiful new inline textarea!
    if (!newText || !newText.trim()) return;

    try {
      // 1. Send the update to the backend
      await renameEntry(entryId, newText.trim());

      // 2. Refresh the UI
      await loadGoalDetails(goalId);
    } catch (err) {
      console.error('Error renaming entry:', err);
      // Optional: Add a toast notification or alert here if the save fails
    }
  };

  const handleDeleteEntry = async (goalId, entryId ) => {
    try {
      await deleteEntry(entryId);
      await loadGoalDetails(goalId);
    } catch (err) {
      console.error(err);
    }
  };

const handleAddEntry = async (goalId) => {
    const text = entryInputs[goalId]?.trim();
    if (!text) return;

    try {
      await addEntry(goalId, text);

      // 1. Clear the specific input field
      setEntryInputs((prev) => ({
        ...prev,
        [goalId]: '',
      }));

      // 2. Refresh the goal details/entries
      if (loadGoalDetails) {
        await loadGoalDetails(goalId);
      }
    } catch (err) {
      console.error('Error adding entry:', err);
    }
  };

const handleChangeEntryInput = (goalId, text) => {
    setEntryInputs((prev) => ({ ...prev, [goalId]: text }));
  };
  
  // We accept 'callbacks' as the third argument for things like reloading data
  const handleRenameGoal = async (goalId, newTitle, callbacks = {}) => {
    const { loadGoals, loadDetails } = callbacks;
    
    const titleToSave = newTitle || window.prompt('Enter new title:');
    if (!titleToSave || titleToSave.trim() === "") return;

    try {
      await renameUserGoal(goalId, titleToSave.trim());
      
      // 1. Optimistic Update (Immediate UI change)
      setGoals(prev => prev.map(g => 
        g.id === goalId ? { ...g, goalTitle: titleToSave.trim() } : g
      ));

      // 2. Trigger optional reloads if they were passed in
      if (loadGoals) await loadGoals();
      if (loadDetails) await loadDetails(goalId);
      
    } catch (err) {
      console.error('Error renaming goal:', err);
    }
  };

const handleDeleteGoal = async (goalId, extraCallback) => {
    if (!window.confirm("Delete permanently?")) return;

    try {
      await deleteUserGoal(goalId);

      // 1. Always update the list
      setGoals(prev => prev.filter(g => g.id !== goalId));

      // 2. ONLY clear these if the page actually provided the functions
      if (setGoalDetails) {
        setGoalDetails(prev => { const c = { ...prev }; delete c[goalId]; return c; });
      }
      if (setGoalCheckDates) {
        setGoalCheckDates(prev => { const c = { ...prev }; delete c[goalId]; return c; });
      }
      if (setOpenGoals) {
        setOpenGoals(prev => { const c = { ...prev }; delete c[goalId]; return c; });
      }

      // 3. Run any final refresh (like loadGoals)
      if (extraCallback) await extraCallback();

    } catch (err) { console.error(err); }
  };

   const handleUpdateCategory = async (goalId, category, useCase) => {
    try {
     await updateGoalCategory(goalId, category, useCase);

      // 2. Update the local goals list state
      setGoals((prev) => prev.map((g) => (g.id === goalId ? { ...g, category, useCase } : g)));

      // 3. If the goal is currently "open" and showing details, update that too
      if (setGoalDetails) {
        setGoalDetails((prev) => {
          if (!prev[goalId]) return prev; // Don't update if it's not open
          return {
            ...prev,
            [goalId]: { ...prev[goalId], category, useCase },
          };
        });
      }
    } catch (err) {
      console.error('Failed to update category:', err);
      alert('Could not save category. Please try again.');
    }
  };

  const handleToggleArchive = async (goalId, onSuccess) => {
    try {
      await toggleArchiveGoal(goalId);
      
      // 1. Optimistic Update: Remove from current view immediately
      setGoals((prev) => prev.filter((g) => g.id !== goalId));

      // 2. Clear any expanded details for this goal
      if (setGoalDetails) {
        setGoalDetails((prev) => { const c = { ...prev }; delete c[goalId]; return c; });
      }

      // 3. Run page-specific refresh (like loadArchivedGoals or loadGlobalContributions)
      if (onSuccess) await onSuccess();
      
    } catch (err) {
      console.error('Failed to toggle archive status:', err);
    }
  };

  const handleViewGoal = async (goalId, storageKey) => {
    if (!goalId) return;

    // 1. Toggle Open State
    setOpenGoals((prev) => {
      const isOpen = prev[goalId] === true;
      const updated = { ...prev, [goalId]: !isOpen };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });

    // 2. If opening, initialize month and load data
    setOpenGoals((currentOpenStates) => {
      const isNowOpen = currentOpenStates[goalId];
      
      if (isNowOpen) {
        // Initialize viewed month if it doesn't exist (mostly for Home page)
        if (setViewedMonths) {
          setViewedMonths((prev) => ({
            ...prev,
            [goalId]: prev[goalId] ?? {
              month: new Date().getMonth(),
              year: new Date().getFullYear(),
            },
          }));
        }

       
          loadGoalDetails(goalId);
        
      }
      return currentOpenStates;
    });
  };

  // Inside your useGoalActions hook
const handleTogglePrivacyForAll = async (goalId, field, currentValue) => {
  const newValue = !currentValue;

  // Update active goals
  setProfile((prev) => ({
    ...prev,
    goals: (prev.goals || []).map((g) =>
      g.id === goalId ? { ...g, [field]: newValue } : g
    ),
  }));

  // ✅ Also update achievements
  setMyAchievements((prev) =>
    prev.map((g) => (g.id === goalId ? { ...g, [field]: newValue } : g))
  );

  try {
    await updateGoalPrivacy(goalId, { [field]: newValue });
    console.log('Privacy updated on server');
  } catch (error) {
    console.error('Error updating privacy:', error);
    // Rollback both
    setProfile((prev) => ({
      ...prev,
      goals: (prev.goals || []).map((g) =>
        g.id === goalId ? { ...g, [field]: currentValue } : g
      ),
    }));
    setMyAchievements((prev) =>
      prev.map((g) => (g.id === goalId ? { ...g, [field]: currentValue } : g))
    );
  }
};

  return { handleRenameGoal,handleDeleteGoal, handleUpdateCategory,handleToggleArchive, 
    handleViewGoal,handleChangeEntryInput,handleAddEntry,handleDeleteEntry,
handleRenameEntry, loadGoalDetails,handleMonthNav,handleOpenList, handleTogglePrivacyForAll };
  
};